// IBAN format + checksum validation per ISO 13616.
//
// Pure spec implementation — the only "constants" here are the universal
// length range (15–34) and the standard letter-to-number mapping
// (A=10…Z=35) baked into the IBAN spec itself. No country-specific tables.
//
// Mod-97 catches single-digit typos and adjacent-digit transpositions
// (the realistic data-entry mistakes). It does not catch a wrong-length
// IBAN whose length still happens to fall in 15–34, which is fine for an
// inline UX hint — backend retains authoritative validation.
export const isValidIBAN = (raw: string): boolean => {
    const iban = raw.replace(/\s+/g, '').toUpperCase();
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;

    const rearranged = iban.slice(4) + iban.slice(0, 4);
    const numeric = rearranged.replace(/[A-Z]/g, (c) => (c.charCodeAt(0) - 55).toString());

    // Piecewise mod-97 to avoid BigInt and Number overflow on long strings.
    let remainder = 0;
    for (const digit of numeric) {
        remainder = (remainder * 10 + Number(digit)) % 97;
    }
    return remainder === 1;
};

// BIC / SWIFT format per ISO 9362.
// 4 letters (bank) + 2 letters (country) + 2 alphanumeric (location)
// + optional 3 alphanumeric (branch). Universal — not country-specific.
const BIC_RE = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

export const isValidBIC = (raw: string): boolean => {
    const bic = raw.replace(/\s+/g, '').toUpperCase();
    return BIC_RE.test(bic);
};
